// Validación del formulario de cotización en el cliente. Su única razón de ser
// es dar aviso inmediato mientras se llena: el servidor sigue siendo la verdad
// (app/models/quote.rb y app/models/quote_item.rb), y estas reglas son un
// espejo deliberado de las de allá. Si una cambia en el modelo, cambia acá.
//
// El riesgo que advierte el comentario de QuoteForm.vue —"que algún día digan
// cosas distintas"— se paga acá: reglas y límites viven en constantes, no
// desperdigados por la plantilla, para que el espejo sea fácil de revisar de
// un vistazo contra el modelo.

// Largos máximos, copiados de las validaciones de Quote.
export const MAX = {
  first_name: 80,
  last_name: 80,
  email: 255,
  street: 160,
  unit: 60,
  comment: 2000
}

// Medidas y cantidad, copiados de QuoteItem (MIN_CM, MAX_CM, MAX_QUANTITY).
export const MEASURE = { min: 20, max: 600 }
export const QUANTITY = { min: 1, max: 30 }
export const MEASUREMENTS = [
  'inside_width_cm',
  'inside_height_cm',
  'outside_width_cm',
  'outside_height_cm'
]

// Chile por defecto: el backend solo acepta celulares válidos (569########),
// porque solo instalamos en la RM. No hay selector de país porque no habría a
// qué otro país cambiarlo sin mentirle a quien lo elige.
export const COUNTRY = { flag: '🇨🇱', code: '+56', name: 'Chile' }

// Espejo de Quote.normalize_phone: deja solo dígitos, saca un 56 de país si
// sobra largo, saca ceros a la izquierda y antepone el 56 cuando queda un
// celular de 9 dígitos que parte en 9. Cualquier otra cosa vuelve tal cual para
// que la validación la marque.
export function normalizePhone (value) {
  let digits = String(value ?? '').replace(/\D/g, '')
  if (digits.length > 9 && digits.startsWith('56')) digits = digits.slice(2)
  digits = digits.replace(/^0+/, '')
  return digits.length === 9 && digits.startsWith('9') ? `56${digits}` : digits
}

export function isValidChileanMobile (value) {
  return /^569\d{8}$/.test(normalizePhone(value))
}

// Correo con el mismo pragmatismo que el resto: exige algo, un @, un dominio y
// un punto en el dominio. El servidor usa URI::MailTo::EMAIL_REGEXP y tiene la
// última palabra; esto solo ataja el error de dedo evidente antes de enviar.
export function isValidEmail (value) {
  const email = String(value ?? '').trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function blank (value) {
  return String(value ?? '').trim() === ''
}

function isInteger (value) {
  return /^-?\d+$/.test(String(value ?? '').trim())
}

// allow_blank del servidor: un campo vacío ya dijo que falta; no le sumamos
// además el error de formato o de largo.
function tooLong (value, max) {
  return String(value ?? '').trim().length > max
}

// Errores de los campos de contacto, con la misma forma que ya usa el
// componente: { nombre_del_campo: [mensajes] }. Solo incluye los campos que
// fallan.
export function validateContact (form, { communes = [] } = {}) {
  const errors = {}
  const add = (field, message) => {
    (errors[field] ||= []).push(message)
  }

  if (blank(form.first_name)) add('first_name', 'Escribe tu nombre')
  else if (tooLong(form.first_name, MAX.first_name)) add('first_name', `Máximo ${MAX.first_name} caracteres`)

  if (blank(form.last_name)) add('last_name', 'Escribe tu apellido')
  else if (tooLong(form.last_name, MAX.last_name)) add('last_name', `Máximo ${MAX.last_name} caracteres`)

  if (blank(form.email)) add('email', 'Escribe tu correo')
  else if (tooLong(form.email, MAX.email)) add('email', `Máximo ${MAX.email} caracteres`)
  else if (!isValidEmail(form.email)) add('email', 'Revisa tu correo, por ejemplo tunombre@correo.cl')

  if (blank(form.phone)) add('phone', 'Escribe tu celular')
  else if (!isValidChileanMobile(form.phone)) {
    add('phone', 'Debe ser un celular válido, por ejemplo +56 9 1234 5678')
  }

  if (blank(form.street)) add('street', 'Escribe tu calle y número')
  else if (tooLong(form.street, MAX.street)) add('street', `Máximo ${MAX.street} caracteres`)

  if (!blank(form.unit) && tooLong(form.unit, MAX.unit)) add('unit', `Máximo ${MAX.unit} caracteres`)

  if (blank(form.commune)) add('commune', 'Elige tu comuna')
  else if (communes.length && !communes.includes(form.commune)) {
    add('commune', 'Por ahora solo instalamos en la Región Metropolitana')
  }

  if (!blank(form.comment) && tooLong(form.comment, MAX.comment)) {
    add('comment', `Máximo ${MAX.comment} caracteres`)
  }

  return errors
}

// Errores de una ventana. Espejo de QuoteItem: producto en catálogo, cuatro
// medidas enteras entre 20 y 600, el borde de fuera no menor que el vano, y
// cantidad entera entre 1 y 30.
export function validateItem (item, { productIds = [] } = {}) {
  const errors = {}
  const add = (field, message) => {
    (errors[field] ||= []).push(message)
  }

  if (blank(item.product_id)) add('product_id', 'Elige una cortina')
  else if (productIds.length && !productIds.includes(item.product_id)) {
    add('product_id', 'Esa cortina no está en el catálogo')
  }

  const outOfRange = (value) =>
    !isInteger(value) || Number(value) < MEASURE.min || Number(value) > MEASURE.max

  for (const field of MEASUREMENTS) {
    if (outOfRange(item[field])) add(field, `Debe ir entre ${MEASURE.min} y ${MEASURE.max} cm`)
  }

  // La comparación vano/marco solo tiene sentido si las cuatro medidas ya son
  // números válidos; si no, el error de rango de arriba ya avisa.
  const allMeasured = MEASUREMENTS.every((field) => !outOfRange(item[field]))
  if (allMeasured) {
    if (Number(item.outside_width_cm) < Number(item.inside_width_cm)) {
      add('outside_width_cm', 'No puede ser menor que el ancho del vano')
    }
    if (Number(item.outside_height_cm) < Number(item.inside_height_cm)) {
      add('outside_height_cm', 'No puede ser menor que el alto del vano')
    }
  }

  if (!isInteger(item.quantity) || Number(item.quantity) < QUANTITY.min || Number(item.quantity) > QUANTITY.max) {
    add('quantity', `Debe ir entre ${QUANTITY.min} y ${QUANTITY.max}`)
  }

  return errors
}

// Valida el formulario completo y devuelve la misma estructura que el
// componente ya llena con los errores del servidor: { base, items }. `valid`
// es true solo si no hay ni un mensaje.
export function validateForm (form, options = {}) {
  const base = validateContact(form, options)
  const items = (form.items || []).map((item) => validateItem(item, options))

  const valid =
    Object.keys(base).length === 0 &&
    items.every((itemErrors) => Object.keys(itemErrors).length === 0)

  return { base, items, valid }
}
